import { ArrowUp, Info, Lightbulb } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';

import { EditorType } from '@/components/tiptap/types/index.enums';
import BlockEditorMarkdown from '@/components/tiptap/vMarkdown/BlockEditor/block-editor';
import DictateControls from '@/container/dictate-button/dictate-controls';
import { useOnboardingFiles } from '@/features/onboarding-v2/hooks/useOnboardingFiles';
import { useTemplateCreationEditor } from '@/features/onboarding-v2/hooks/useTemplateCreationEditor';
import { cn } from '@/lib/cn';
import { type ClinicianTypeInput } from '@/lib/types';
import {
  Banner,
  BannerContent,
  BannerDescription,
  BannerTitle,
} from '@heidi/design-system/banner';
import { Button } from '@heidi/design-system/button';
import { Card } from '@heidi/design-system/card';
import { TypographyH2, TypographyP } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { type CountryType } from '@heidi/utils/countries';

import { OnboardingFiles } from '../shared/OnboardingFiles';
import { OnboardingUploadFileButton } from '../shared/OnboardingUploadFileButton';

interface TemplateCreationStepProps {
  templateDescription: string;
  specialty: ClinicianTypeInput | null;
  country: CountryType | null;
  onTemplateDescriptionChange: (value: string) => void;
  onCreateTemplate: () => void;
  onSkip: () => void;
  isValid: boolean;
  isLoading?: boolean;
}

export function TemplateCreationStep({
  templateDescription,
  specialty,
  country,
  onTemplateDescriptionChange,
  onCreateTemplate,
  onSkip,
  isValid,
  isLoading = false,
}: TemplateCreationStepProps) {
  const { t } = useTranslations();

  const {
    editor,
    isFocused,
    suggestions,
    isSuggestionsPending,
    handleSuggestionClick,
    handleCardClick,
  } = useTemplateCreationEditor({
    templateDescription,
    specialty,
    country,
    onTemplateDescriptionChange,
    isLoading,
  });

  const { files, hasPendingUploads } = useOnboardingFiles();

  const hasCompletedFiles = files.some((file) => file.status === 'done');
  const canSubmit = (isValid || hasCompletedFiles) && !hasPendingUploads;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canSubmit && !isLoading) {
      onCreateTemplate();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col gap-6">
      <div className="flex flex-col gap-6">
        <TypographyH2>{t('signup.createTemplateWithAI')}</TypographyH2>
        <TypographyP className="text-text-primary">
          {t('signup.templateDescriptionHint')}
        </TypographyP>
      </div>

      <div className="flex flex-col gap-2">
        <Card
          className={cn(
            'flex h-[148px] w-[400px] flex-col justify-between border border-border transition',
            isFocused && 'border-border-pressed',
            isLoading && 'border-border-disabled'
          )}
          onClick={handleCardClick}
          id="onboarding-template-editor-container"
          data-testid="onboarding-template-editor-container"
        >
          <div className="flex-1 overflow-y-auto px-4 pt-2.5">
            {editor && (
              <BlockEditorMarkdown
                testTag="onboarding-template-editor"
                editor={editor}
                editorType={EditorType.TEMPLATE}
                enableFindReplace={false}
                enableSnippet={false}
                enableBubbleMenu={true}
                tabType={undefined}
                className="text-sm"
              />
            )}
          </div>
          <div className="flex flex-col gap-2 p-4">
            <OnboardingFiles />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <OnboardingUploadFileButton isPending={isLoading} />
              </div>
              <div className="flex items-center gap-1">
                <DictateControls
                  editor={editor}
                  disabled={isLoading}
                  source="createTemplate"
                  size="md"
                />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="submit"
                      size="md"
                      iconOnly
                      disabled={!canSubmit || isLoading}
                    >
                      <ArrowUp size={14} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">{t('signup.create')}</TooltipContent>
                </Tooltip>
              </div>
            </div>
          </div>
        </Card>

        <div className="relative w-[400px]">
          <div className="flex h-7 items-center gap-2 overflow-x-auto pr-12 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {isSuggestionsPending && suggestions.length === 0 ? (
              <>
                <div className="h-7 w-40 shrink-0 animate-pulse rounded-lg bg-background-secondary" />
                <div className="h-7 w-32 shrink-0 animate-pulse rounded-lg bg-background-secondary" />
              </>
            ) : (
              suggestions.map((suggestion) => (
                <Button
                  key={suggestion}
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={() => handleSuggestionClick(suggestion)}
                  disabled={isLoading}
                  className="shrink-0 gap-1.5"
                >
                  <Lightbulb size={12} />
                  {suggestion}
                </Button>
              ))
            )}
          </div>
          <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-surface-accent to-transparent" />
        </div>
      </div>

      <Banner subVariant="default" className="w-[400px]">
        <BannerContent icon={<Info />}>
          <BannerTitle>{t('signup.haveExistingNote')}</BannerTitle>
          <BannerDescription>{t('signup.uploadNoteHint')}</BannerDescription>
        </BannerContent>
      </Banner>

      <div className="flex flex-1 items-end pt-4">
        <Button
          size="lg"
          type="button"
          variant="ghost"
          onClick={onSkip}
          disabled={isLoading}
          className="w-full"
        >
          {t('signup.skipTemplate')}
        </Button>
      </div>
    </form>
  );
}
